import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import { RefreshCcw, Users, Clock } from 'lucide-react';
import { getLobby } from '@/lib/api/endpoints/rounds';
import { queryKeys } from '@/lib/api/queryKeys';
import { Button } from '@/design-system/components/Button';
import { Modal } from '@/design-system/components/Modal';
import { Input } from '@/design-system/components/Input';
import { EmptyState } from '@/design-system/components/EmptyState';
import { Badge } from '@/design-system/components/Badge';
import { useSession } from '@/store/sessionStore';
import { t } from '@/lib/i18n';
import type { RoundDto } from '@/shared/types/contracts';
import styles from './LobbyPage.module.css';

export default function LobbyPage() {
  const navigate = useNavigate();
  const { user } = useSession();
  const [enterRound, setEnterRound] = useState<RoundDto | null>(null);
  const [pin, setPin] = useState('');
  const [pinError, setPinError] = useState('');

  const { data: rounds = [], isLoading, refetch } = useQuery({
    queryKey: queryKeys.lobby.openRounds,
    queryFn: getLobby,
    refetchInterval: 15_000,
  });

  const handleJoin = (round: RoundDto) => {
    if (round.requires_pin) {
      setEnterRound(round);
      setPin('');
      setPinError('');
    } else {
      navigate(`/rounds/${round.id}/waiting`);
    }
  };

  const handlePinSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!enterRound) return;
    if (pin.trim().length === 0) { setPinError(t('lobby.pinValidation')); return; }
    // PIN được truyền qua state để WaitingRoomPage emit khi join
    navigate(`/rounds/${enterRound.id}/waiting`, { state: { pin } });
    setEnterRound(null);
  };

  return (
    <div className={styles.root}>
      <div className={styles.hero}>
        <h1 className={styles.heroTitle}>
          {t('lobby.greeting', { name: user?.display_name?.split(' ').slice(-1)[0] ?? user?.display_name ?? '👋' })}
        </h1>
        <p className={styles.heroSub}>{t('lobby.subtitle')}</p>
      </div>

      <div className={styles.content}>
        <div className={styles.sectionHeader}>
          <h2 className={styles.sectionTitle}>{t('lobby.sectionTitle')}</h2>
          <button className={styles.refreshBtn} onClick={() => refetch()} title={t('lobby.refresh')}>
            <RefreshCcw size={16} />
          </button>
        </div>

        {isLoading ? (
          <div className={styles.loading}>{t('common.loading')}</div>
        ) : rounds.length === 0 ? (
          <EmptyState
            title={t('lobby.emptyTitle')}
            description={t('lobby.emptyDescription')}
          />
        ) : (
          <div className={styles.grid}>
            {rounds.map((r) => (
              <RoundCard key={r.id} round={r} onJoin={() => handleJoin(r)} />
            ))}
          </div>
        )}
      </div>

      {/* PIN Modal */}
      <Modal
        open={!!enterRound}
        onOpenChange={(o) => { if (!o) setEnterRound(null); }}
        title={t('lobby.pinModalTitle')}
      >
        <form onSubmit={handlePinSubmit} className={styles.pinForm}>
          <p className={styles.pinHint}>
            {t('lobby.pinHint', { name: enterRound?.name ?? '' })}
          </p>
          <Input
            placeholder={t('lobby.pinPlaceholder')}
            value={pin}
            onChange={(e) => { setPin(e.target.value); setPinError(''); }}
            autoFocus
            maxLength={6}
          />
          {pinError && <span className={styles.pinError}>{pinError}</span>}
          <div className={styles.pinActions}>
            <Button variant="secondary" type="button" onClick={() => setEnterRound(null)}>{t('common.cancel')}</Button>
            <Button type="submit">{t('lobby.enterRoom')}</Button>
          </div>
        </form>
      </Modal>
    </div>
  );
}

function RoundCard({ round, onJoin }: { round: RoundDto; onJoin: () => void }) {
  const isOpen = round.status === 'OPEN';
  const isInProgress = round.status === 'IN_PROGRESS';

  return (
    <div className={styles.card}>
      <div className={styles.cardTop}>
        <span className={`${styles.statusDot} ${isInProgress ? styles.statusInProgress : styles.statusOpen}`} />
        <div className={styles.cardStatus}>
          {isOpen ? t('lobby.statusOpen') : t('lobby.statusPlaying')}
        </div>
        {round.requires_pin && <Badge tone="warning">PIN</Badge>}
      </div>

      <h3 className={styles.cardTitle}>{round.name}</h3>
      {round.description && <p className={styles.cardDesc}>{round.description}</p>}

      {round.host && (
        <div className={styles.cardMeta}>
          {t('lobby.host', { name: round.host.display_name ?? 'Admin' })}
        </div>
      )}

      <div className={styles.cardInfo}>
        <span className={styles.infoItem}><Clock size={13} />{t('lobby.timePerQuestion', { n: round.time_limit_per_question_sec })}</span>
        <span className={styles.infoItem}><Users size={13} />{t('lobby.players', { n: round.participants_count ?? 0 })}</span>
      </div>

      <Button
        className={styles.joinBtn}
        variant={isInProgress ? 'secondary' : 'primary'}
        onClick={onJoin}
      >
        {isInProgress ? t('lobby.watchAsSpectator') : t('lobby.joinRoom')}
      </Button>
    </div>
  );
}
